// Building constructors and the board mutations of buildings (add / remove / move / restore). Every change of
// `state.buildings` goes through here, so the id order (ascending), the occupancy index and the chute
// topology (dropped on every change, rebuilt lazily) stay in sync.

import {
  crateTiers,
  defaultMode,
  isMatcher,
  isSimpleProc,
  matcherDef,
  packerDef,
  type SimpleProcType,
} from './bdefs.ts';
import { opposite } from './core/grid.ts';
import { indexById, insertById } from './core/sort.ts';
import type { Dir, Int } from './core/types.ts';
import type { Ctx } from './ctx.ts';
import type { SimDefs } from './defs.ts';
import { newId } from './entities.ts';
import type { BType, OreId } from './gen/ids.ts';
import { occAt, occSet } from './occupancy.ts';
import { zeroRing } from './readout.ts';
import type {
  BBase,
  Building,
  ChuteB,
  CrateB,
  DepositB,
  DrillB,
  GalleryB,
  GalleryWin,
  PackerB,
  ProcessorB,
  ProcessorType,
  SimState,
} from './state.ts';

function base(id: Int, x: Int, y: Int, dir: Dir, level: Int): Omit<BBase, 'type'> {
  return { id, level, x, y, dir, paid: 0, ghost: null, paused: false, stall: null, stallT: 0 };
}

function zeros(n: Int): Int[] {
  const a: Int[] = [];
  for (let i = 0; i < n; i++) a.push(0);
  return a;
}

export function makeDeposit(s: SimState, ore: OreId, x: Int, y: Int, level: Int): DepositB {
  return {
    ...base(newId(s), x, y, 'N', level),
    type: 'deposit',
    ore,
    acc: 0,
    rr: 0,
    blocked: false,
  };
}

export function makeDrill(s: SimState, x: Int, y: Int, dir: Dir, level: Int): DrillB {
  return { ...base(newId(s), x, y, dir, level), type: 'drill', t: 0 };
}

export function makeCrate(s: SimState, d: SimDefs, x: Int, y: Int, dir: Dir, level: Int): CrateB {
  return {
    ...base(newId(s), x, y, dir, level),
    type: 'crate',
    fam: null,
    counts: zeros(crateTiers(d)),
  };
}

/** A chute moving items from its `inDir` side out through `dir`. */
export function makeChute(s: SimState, x: Int, y: Int, dir: Dir, inDir: Dir): ChuteB {
  return { ...base(newId(s), x, y, dir, 1), type: 'chute', inDir, carry: null, prog: 0 };
}

/** An empty processor (Caster, Grinder, Smelter, Press, Loader, Gunsmith) in its default mode. */
export function makeProcessor(
  s: SimState,
  d: SimDefs,
  type: ProcessorType,
  x: Int,
  y: Int,
  dir: Dir,
  level: Int,
): ProcessorB {
  let nSlots = 1;
  let mode: string | null = null;
  if (isMatcher(type)) nSlots = matcherDef(d, type).slots.length;
  else if (isSimpleProc(type)) mode = defaultMode(d, type as SimpleProcType);
  const slots: ProcessorB['slots'] = [];
  for (let i = 0; i < nSlots; i++) slots.push([]);
  return {
    ...base(newId(s), x, y, dir, level),
    type,
    slots,
    heat: 0,
    prog: 0,
    proc: null,
    out: [],
    mode,
    busy: zeroRing(d),
  };
}

/** An empty Packer whose target is its level cap (R5: default target = the cap). */
export function makePacker(s: SimState, d: SimDefs, x: Int, y: Int, dir: Dir, level: Int): PackerB {
  return {
    ...base(newId(s), x, y, dir, level),
    type: 'packer',
    buf: [],
    target: packerDef(d).targetCapByLevel[level - 1] ?? 1,
    prog: 0,
    lockFam: null,
    out: [],
    busy: zeroRing(d),
  };
}

export function galleryWindow(d: SimDefs): GalleryWin {
  return {
    ready: zeroRing(d),
    idle: zeroRing(d),
    full: zeroRing(d),
    fired: zeroRing(d),
    supplied: zeroRing(d),
    paid: zeroRing(d),
    idx: 0,
    n: 0,
  };
}

/** A Gallery (level 1, no gun, empty queue). The caller mounts the gun (and books it as granted). */
export function makeGallery(s: SimState, d: SimDefs, x: Int, y: Int, dir: Dir): GalleryB {
  return {
    ...base(newId(s), x, y, dir, 1),
    type: 'gallery',
    gun: null,
    queue: [],
    cd: 0,
    practice: 0,
    manual: false,
    win: galleryWindow(d),
  };
}

/** Any non-deposit building type in its empty starting state (the Gallery without a gun). */
export function makeOfType(
  s: SimState,
  d: SimDefs,
  type: Exclude<BType, 'deposit'>,
  x: Int,
  y: Int,
  dir: Dir,
  level: Int,
): Building {
  switch (type) {
    case 'drill':
      return makeDrill(s, x, y, dir, level);
    case 'crate':
      return makeCrate(s, d, x, y, dir, level);
    case 'chute':
      return makeChute(s, x, y, dir, opposite(dir));
    case 'packer':
      return makePacker(s, d, x, y, dir, level);
    case 'gallery': {
      const g = makeGallery(s, d, x, y, dir);
      g.level = level;
      return g;
    }
    case 'dock':
      return { ...base(newId(s), x, y, dir, level), type: 'dock', queue: [], prog: 0 };
    default:
      return makeProcessor(s, d, type, x, y, dir, level);
  }
}

/** Appends a freshly made building (its id is the newest, so id order holds) and indexes its cell. */
export function addBuilding(c: Ctx, b: Building): void {
  c.s.buildings.push(b);
  occSet(c.occ, b.x, b.y, b.id);
  c.topo = null;
}

/** Puts back a building with its original id (build undo), keeping id order. The cell must be free. */
export function restoreBuilding(c: Ctx, b: Building): void {
  insertById(c.s.buildings, b);
  occSet(c.occ, b.x, b.y, b.id);
  c.topo = null;
}

/** Takes a building off the board (its contents stay on the returned object for the caller). */
export function removeBuilding(c: Ctx, id: Int): Building {
  const i = indexById(c.s.buildings, id);
  if (i < 0) throw new RangeError(`no building ${id}`);
  const b = c.s.buildings[i] as Building;
  c.s.buildings.splice(i, 1);
  if (occAt(c.occ, b.x, b.y) === id) occSet(c.occ, b.x, b.y, 0);
  if (c.s.transient.held === id) c.s.transient.held = null;
  c.topo = null;
  return b;
}

/** Moves a building to a cell the caller made free (or is swapping into). */
export function moveBuilding(c: Ctx, b: Building, x: Int, y: Int): void {
  if (occAt(c.occ, b.x, b.y) === b.id) occSet(c.occ, b.x, b.y, 0);
  b.x = x;
  b.y = y;
  occSet(c.occ, x, y, b.id);
  c.topo = null;
}

/** Changes a building's facing (and a chute's in-side). */
export function setFacing(c: Ctx, b: Building, dir: Dir, inDir: Dir | null): void {
  b.dir = dir;
  if (b.type === 'chute' && inDir !== null) b.inDir = inDir;
  c.topo = null;
}
